import { useQuery } from '@tanstack/react-query'
import {
  CheckSquare,
  Plus,
  ListChecks,
  Clock,
  Calendar,
  User,
  Globe,
  Lock,
  Trash2,
  Undo2,
  CheckCheck,
  RefreshCw,
  AlertCircle,
  FileText,
  MoreHorizontal,
  Loader2,
  ArrowUpRight,
} from 'lucide-react'
import { useState, useCallback, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DateInput } from '@/components/ui/date-input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { usePermission, useAllowedActionsBatch } from '@/hooks/usePermission'
import {
  useTasksFeed,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
  useCompleteTask,
  useReopenTask,
} from '@/hooks/useTasks'
import { useWorkspace } from '@/hooks/useWorkspace'
import { getWorkspaceUsers } from '@/services/api/task.service'
import type { Task, TaskFeedItem } from '@/services/api/task.service'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import { cn } from '@/utils/cn'
import { formatDate, formatDateInput } from '@/utils/date'

type SectionTab = 'remaining' | 'completed'
type EditableTask = Task & { instructions?: string | null }

function getLocalDateInputValue(date: Date = new Date()) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function FormattedDateInput({ id, label, ...props }: { id: string; label: string; value: string; min?: string; max?: string; onChange: (value: string) => void }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs font-medium text-neutral-500">
        {label}
      </Label>
      <DateInput id={id} aria-label={`${label} date`} {...props} />
    </div>
  )
}

function NotePromptDialog({
  open,
  title,
  description,
  confirmLabel,
  loading,
  showNextActivity,
  defaultNextActivityAt,
  defaultNextActivityInstructions,
  onOpenChange,
  onConfirm,
}: {
  open: boolean
  title: string
  description: string
  confirmLabel: string
  loading: boolean
  showNextActivity?: boolean
  defaultNextActivityAt?: string
  defaultNextActivityInstructions?: string
  onOpenChange: (open: boolean) => void
  onConfirm: (payload: { note: string; nextActivityAt?: string; nextActivityInstructions?: string }) => Promise<void>
}) {
  const [note, setNote] = useState('')
  const [nextActivityAt, setNextActivityAt] = useState('')
  const [nextActivityInstructions, setNextActivityInstructions] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onConfirm({
      note: note.trim(),
      nextActivityAt: nextActivityAt || undefined,
      nextActivityInstructions: nextActivityInstructions.trim() || undefined,
    })
    setNote('')
    setNextActivityAt('')
    setNextActivityInstructions('')
    onOpenChange(false)
  }

  useEffect(() => {
    if (open) {
      if (defaultNextActivityAt) setNextActivityAt(defaultNextActivityAt)
      if (defaultNextActivityInstructions) setNextActivityInstructions(defaultNextActivityInstructions)
    }
  }, [open, defaultNextActivityAt, defaultNextActivityInstructions])

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!loading) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="action-note">Note (optional)</Label>
            <Textarea
              id="action-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={2000}
              placeholder="Add context for this action"
              className="min-h-[100px]"
            />
          </div>
          {showNextActivity && (
            <>
              <div className="space-y-2">
                <Label htmlFor="next-activity">Next activity date (optional)</Label>
                <DateInput id="next-activity" value={nextActivityAt} onChange={setNextActivityAt} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="next-instructions">Instructions (optional)</Label>
                <Textarea
                  id="next-instructions"
                  value={nextActivityInstructions}
                  onChange={(e) => setNextActivityInstructions(e.target.value)}
                  maxLength={500}
                  placeholder="What should happen next?"
                  className="min-h-[60px]"
                />
              </div>
            </>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={loading}>Cancel</Button>
            </DialogClose>
            <Button type="submit" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ─── Create Task Dialog ─────────────────────────────────────────────────────

function CreateTaskDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { currentWorkspaceId } = useWorkspace()
  const createTask = useCreateTask()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [visibilityScope, setVisibilityScope] = useState<'ASSIGNED' | 'WORKSPACE'>('ASSIGNED')
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([])
  const [instructions, setInstructions] = useState('')

  const { data: workspaceUsers = [], isLoading: usersLoading } = useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'users'],
    queryFn: getWorkspaceUsers,
    enabled: !!currentWorkspaceId,
    staleTime: 60 * 1000,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    try {
      await createTask.mutateAsync({
        title: title.trim(),
        description: description.trim() || undefined,
        visibilityScope,
        dueDate: dueDate || undefined,
        assigneeUserIds: selectedAssignees,
        instructions: instructions.trim() || null,
      })
      toast.success('Task created')
      setTitle('')
      setDescription('')
      setDueDate('')
      setVisibilityScope('ASSIGNED')
      setSelectedAssignees([])
      setInstructions('')
      onOpenChange(false)
    } catch {
      toast.error('Failed to create task')
    }
  }

  const toggleAssignee = (userId: string) => {
    setSelectedAssignees((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    )
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Task</DialogTitle>
          <DialogDescription>
            Add a new task to track follow-ups and reminders.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="task-title">Title</Label>
            <Input
              id="task-title"
              placeholder="What needs to be done?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              maxLength={200}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="instructions">Instructions</Label>
            <Textarea
              id="instructions"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Optional follow-up instructions"
              maxLength={2000}
              className="min-h-[80px]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="task-desc">Description (optional)</Label>
            <Textarea
              id="task-desc"
              placeholder="Add details…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              className="min-h-[80px]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="task-due">Due date</Label>
              <DateInput id="task-due" value={dueDate} onChange={setDueDate} />
            </div>
            <div className="space-y-2">
              <Label>Visibility</Label>
              <div className="flex items-center gap-3 rounded-md border border-neutral-200 px-3 py-2">
                {visibilityScope === 'WORKSPACE' ? (
                  <Globe className="h-4 w-4 text-neutral-500" />
                ) : (
                  <Lock className="h-4 w-4 text-neutral-500" />
                )}
                <span className="flex-1 text-sm">
                  {visibilityScope === 'WORKSPACE' ? 'Workspace' : 'Assigned only'}
                </span>
                <Switch
                  checked={visibilityScope === 'WORKSPACE'}
                  onCheckedChange={(checked) =>
                    setVisibilityScope(checked ? 'WORKSPACE' : 'ASSIGNED')
                  }
                  aria-label="Toggle visibility"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Assignees</Label>
            {usersLoading ? (
              <div className="flex items-center gap-2 text-sm text-neutral-400">
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading users…
              </div>
            ) : (
              <div className="max-h-32 space-y-1 overflow-y-auto rounded-md border border-neutral-200 p-1">
                {workspaceUsers.map((u) => (
                  <button
                    key={u.userId}
                    type="button"
                    onClick={() => toggleAssignee(u.userId)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors',
                      selectedAssignees.includes(u.userId)
                        ? 'bg-primary-100 text-primary-800'
                        : 'hover:bg-neutral-100 text-neutral-700'
                    )}
                  >
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-neutral-200 text-xs font-medium text-neutral-600">
                      {u.name.charAt(0).toUpperCase()}
                    </div>
                    <span>{u.name}</span>
                    {selectedAssignees.includes(u.userId) && (
                      <CheckCheck className="ml-auto h-3.5 w-3.5 text-primary-600" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">Cancel</Button>
            </DialogClose>
            <Button type="submit" disabled={!title.trim() || createTask.isPending}>
              {createTask.isPending ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Creating…</>
              ) : (
                <><Plus className="h-4 w-4" /> Create Task</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ─── Edit Task Dialog ───────────────────────────────────────────────────────

function EditTaskDialog({
  task,
  open,
  onOpenChange,
}: {
  task: EditableTask
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const updateTask = useUpdateTask()
  const [title, setTitle] = useState(task.title)
  const [description, setDescription] = useState(task.description ?? '')
  const [dueDate, setDueDate] = useState(task.dueDate ? formatDateInput(task.dueDate) : '')
  const [visibilityScope, setVisibilityScope] = useState<'ASSIGNED' | 'WORKSPACE'>(task.visibilityScope)
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>(task.assigneeUserIds)
  const [instructions, setInstructions] = useState(task.instructions ?? '')

  const { currentWorkspaceId } = useWorkspace()
  const { data: workspaceUsers = [] } = useQuery({
    queryKey: ['workspace', currentWorkspaceId, 'users'],
    queryFn: getWorkspaceUsers,
    enabled: !!currentWorkspaceId,
    staleTime: 60 * 1000,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    try {
      await updateTask.mutateAsync({
        taskId: task.id,
        data: {
          title: title.trim(),
          description: description.trim() || undefined,
          visibilityScope,
          dueDate: dueDate || undefined,
          assigneeUserIds: selectedAssignees,
          instructions: instructions.trim() || null,
        },
      })
      toast.success('Task updated')
      onOpenChange(false)
    } catch {
      toast.error('Failed to update task')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Task</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-title">Title</Label>
            <Input id="edit-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-desc">Description</Label>
            <Textarea id="edit-desc" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} className="min-h-[80px]" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-instructions">Instructions</Label>
            <Textarea id="edit-instructions" value={instructions} onChange={(e) => setInstructions(e.target.value)} maxLength={2000} className="min-h-[80px]" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-due">Due date</Label>
              <DateInput id="edit-due" value={dueDate} onChange={setDueDate} />
            </div>
            <div className="space-y-2">
              <Label>Visibility</Label>
              <div className="flex items-center gap-3 rounded-md border border-neutral-200 px-3 py-2">
                {visibilityScope === 'WORKSPACE' ? <Globe className="h-4 w-4 text-neutral-500" /> : <Lock className="h-4 w-4 text-neutral-500" />}
                <span className="flex-1 text-sm">{visibilityScope === 'WORKSPACE' ? 'Workspace' : 'Assigned only'}</span>
                <Switch checked={visibilityScope === 'WORKSPACE'} onCheckedChange={(c) => setVisibilityScope(c ? 'WORKSPACE' : 'ASSIGNED')} />
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Assignees</Label>
            <div className="max-h-32 space-y-1 overflow-y-auto rounded-md border border-neutral-200 p-1">
              {workspaceUsers.map((u) => (
                <button key={u.userId} type="button" onClick={() => setSelectedAssignees((p) => p.includes(u.userId) ? p.filter((id) => id !== u.userId) : [...p, u.userId])}
                  className={cn('flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors',
                    selectedAssignees.includes(u.userId) ? 'bg-primary-100 text-primary-800' : 'hover:bg-neutral-100 text-neutral-700')}>
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-neutral-200 text-xs font-medium text-neutral-600">{u.name.charAt(0).toUpperCase()}</div>
                  <span>{u.name}</span>
                  {selectedAssignees.includes(u.userId) && <CheckCheck className="ml-auto h-3.5 w-3.5 text-primary-600" />}
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
            <Button type="submit" disabled={!title.trim() || updateTask.isPending}>{updateTask.isPending ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}


// ─── Feed Item Card ─────────────────────────────────────────────────────────

function FeedItemCard({
  item,
  onComplete,
  onReopen,
  onDelete,
  onNavigate,
  isCompleting,
  isReopening,
  isDeleting,
  onEdit,
  canDelete = true,
}: {
  item: TaskFeedItem
  canDelete?: boolean
  onComplete: () => void
  onReopen: () => void
  onDelete: () => void
  onNavigate?: () => void
  isCompleting: boolean
  isReopening: boolean
  isDeleting: boolean
  onEdit?: () => void
}) {
  const isTask = item.type === 'TASK'
  const isFollowUp = !isTask
  const isOverdue = !item.completed && item.dueDate && new Date(item.dueDate) < new Date(new Date().toDateString())

  return (
    <div
      onClick={isFollowUp ? onNavigate : undefined}
      role={isFollowUp ? 'button' : undefined}
      tabIndex={isFollowUp ? 0 : undefined}
      onKeyDown={isFollowUp ? (e) => { if (e.key === 'Enter') onNavigate?.() } : undefined}
      className={cn(
      'group relative flex items-start gap-4 rounded-lg border p-4 transition-all',
      item.completed ? 'border-neutral-200 bg-neutral-50/50' : 'border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-sm',
      isOverdue && !item.completed && 'border-l-orange-400 border-l-4',
      isFollowUp && 'cursor-pointer',
    )}>
      <div className="mt-0.5 shrink-0">
        {item.completed ? (
          <button
            onClick={(e) => { if (isFollowUp) { e.stopPropagation(); onNavigate?.() } else { onReopen() } }}
            disabled={isReopening}
            className="flex h-6 w-6 items-center justify-center rounded-full bg-success-100 text-success-700 transition-colors hover:bg-success-200"
            aria-label="Reopen">
            {isReopening ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCheck className="h-3.5 w-3.5" />}
          </button>
        ) : (
          <button
            onClick={(e) => { if (isFollowUp) { e.stopPropagation(); onNavigate?.() } else { onComplete() } }}
            disabled={isCompleting}
            className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-neutral-300 text-transparent transition-colors hover:border-primary-400 hover:text-primary-500"
            aria-label="Mark complete">
            {isCompleting ? <Loader2 className="h-3.5 w-3.5 animate-spin text-primary-500" /> : <CheckCheck className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className={cn('text-sm font-medium leading-snug', item.completed ? 'text-neutral-400 line-through' : 'text-neutral-900')}>
              {item.title}
            </h3>
            {item.description && <p className="mt-0.5 text-sm text-neutral-500 line-clamp-2">{item.description}</p>}
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {!isTask && (
              <Badge variant="secondary" className="text-[10px]">
                <FileText className="mr-1 h-3 w-3" />
                Follow-up
              </Badge>
            )}
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-400">
          {item.dueDate && (
            <span className={cn('flex items-center gap-1', isOverdue && 'text-orange-600 font-medium')}>
              <Calendar className="h-3 w-3" />
              {formatDate(item.dueDate)}
              {isOverdue && ' (Overdue)'}
            </span>
          )}
          {item.assigneeUserIds.length > 0 && (
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {item.assigneeUserIds.length} assignee{item.assigneeUserIds.length > 1 ? 's' : ''}
            </span>
          )}
          {item.completedAt && (() => {
            const showCompletedBadge = item.type === 'RECORD_FOLLOWUP' ? !item.dueDate : true;
            return showCompletedBadge ? (
              <span className="flex items-center gap-1">
                <CheckCheck className="h-3 w-3 text-success-600" />
                Completed {formatDate(item.completedAt)}
              </span>
            ) : null;
          })()}
          {item.instructions && (
            <span className="flex items-center gap-1 rounded-md border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-xs text-neutral-600">
              <FileText className="h-3 w-3" />
              <span className="line-clamp-2">{item.instructions}</span>
            </span>
          )}
        </div>
      </div>

      <div
        className={cn("shrink-0 transition-opacity", item.completed ? "opacity-100" : "opacity-0 group-hover:opacity-100")}
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            {!item.completed && isTask && onEdit && (
              <DropdownMenuItem onClick={onEdit}>
                <ListChecks className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
            )}
            {isFollowUp ? (
              <DropdownMenuItem onClick={onNavigate}>
                <ArrowUpRight className="mr-2 h-4 w-4" />
                Open record
              </DropdownMenuItem>
            ) : item.completed ? (
              <DropdownMenuItem onClick={onReopen} disabled={isReopening}>
                <Undo2 className="mr-2 h-4 w-4" />
                Reopen
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={onComplete} disabled={isCompleting}>
                <CheckCheck className="mr-2 h-4 w-4" />
                Mark complete
              </DropdownMenuItem>
            )}
            {isTask && canDelete && (
              <DropdownMenuItem onClick={onDelete} disabled={isDeleting} className="text-destructive-600">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}


// ─── Feed Section ───────────────────────────────────────────────────────────

function FeedSection({
  title,
  icon: Icon,
  items,
  onComplete,
  onReopen,
  onDelete,
  onNavigate,
  onEdit,
  completingIds,
  reopeningIds,
  deletingIds,
  canEditTask = () => true,
  canDeleteTask = () => true,
}: {
  canEditTask?: (item: TaskFeedItem) => boolean
  canDeleteTask?: (item: TaskFeedItem) => boolean
  title: string
  icon: React.ElementType
  items: TaskFeedItem[]
  onComplete: (item: TaskFeedItem) => void
  onReopen: (item: TaskFeedItem) => void
  onDelete: (item: TaskFeedItem) => void
  onNavigate: (item: TaskFeedItem) => void
  onEdit?: (item: TaskFeedItem) => void
  completingIds: string[]
  reopeningIds: string[]
  deletingIds: string[]
}) {
  if (items.length === 0) return null

  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4 text-neutral-400" />
        <h2 className="text-xs font-semibold uppercase tracking-widest text-neutral-400">{title}</h2>
        <span className="ml-auto text-xs text-neutral-400">{items.length} item{items.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="space-y-2">
        {items.map((item) => {
          const itemKey = `${item.type}-${item.id}`
          return (
            <FeedItemCard
              key={itemKey}
              item={item}
              onComplete={() => onComplete(item)}
              onReopen={() => onReopen(item)}
              onDelete={() => onDelete(item)}
              onNavigate={() => onNavigate(item)}
              onEdit={item.type === 'TASK' && canEditTask(item) ? () => onEdit?.(item) : undefined}
              canDelete={canDeleteTask(item)}
              isCompleting={completingIds.includes(itemKey)}
              isReopening={reopeningIds.includes(itemKey)}
              isDeleting={deletingIds.includes(itemKey)}
            />
          )
        })}
      </div>
    </section>
  )
}


// ─── Main Tasks Page ────────────────────────────────────────────────────────

export default function TasksPage() {
  const navigate = useNavigate()
  const [section, setSection] = useState<SectionTab>('remaining')
  const todayDate = useMemo(() => getLocalDateInputValue(), [])
  const [dateFrom, setDateFrom] = useState(todayDate)
  const [dateTo, setDateTo] = useState(todayDate)
  const dateRange = useMemo(() => ({ from: dateFrom, to: dateTo }), [dateFrom, dateTo])
  const { data: feed, isLoading, isError, refetch } = useTasksFeed(section, dateRange)
  const [createOpen, setCreateOpen] = useState(false)
  const canCreateTask = usePermission().can(PERMISSION_KEYS.TASK_CREATE)
  const [editTask, setEditTask] = useState<EditableTask | null>(null)
  const [completingIds, setCompletingIds] = useState<string[]>([])
  const [reopeningIds, setReopeningIds] = useState<string[]>([])
  const [deletingIds, setDeletingIds] = useState<string[]>([])
  const [actionItem, setActionItem] = useState<TaskFeedItem | null>(null)
  const [actionMode, setActionMode] = useState<'complete' | 'reopen' | null>(null)

  const completeTask = useCompleteTask()
  const reopenTask = useReopenTask()
  const deleteTask = useDeleteTask()

  const isTodayRange = dateFrom === todayDate && dateTo === todayDate
  const rangeLabel = dateFrom === dateTo
    ? formatDate(dateFrom)
    : `${formatDate(dateFrom)} - ${formatDate(dateTo)}`

  const handleDateFromChange = useCallback((value: string) => {
    if (!value) return
    setDateFrom(value)
    setDateTo((currentTo) => value > currentTo ? value : currentTo)
  }, [])

  const handleDateToChange = useCallback((value: string) => {
    if (!value) return
    setDateTo(value)
    setDateFrom((currentFrom) => value < currentFrom ? value : currentFrom)
  }, [])

  const resetDateRange = useCallback(() => {
    setDateFrom(todayDate)
    setDateTo(todayDate)
  }, [todayDate])

  const runComplete = useCallback(async (item: TaskFeedItem, payload: { note: string | null; nextActivityAt?: string; nextActivityInstructions?: string }) => {
    const itemKey = `${item.type}-${item.id}`
    setCompletingIds((prev) => [...prev, itemKey])
    try {
      await completeTask.mutateAsync({ taskId: item.id, data: { note: payload.note } })
      toast.success("Marked complete")
    } catch {
      toast.error("Failed to complete")
    } finally {
      setCompletingIds((prev) => prev.filter((id) => id !== itemKey))
    }
  }, [completeTask])

  const runReopen = useCallback(async (item: TaskFeedItem, payload: { note: string | null; nextActivityAt?: string; nextActivityInstructions?: string }) => {
    const itemKey = `${item.type}-${item.id}`
    setReopeningIds((prev) => [...prev, itemKey])
    try {
      await reopenTask.mutateAsync({ taskId: item.id, data: { note: payload.note, nextActivityAt: payload.nextActivityAt, nextActivityInstructions: payload.nextActivityInstructions } })
      toast.success("Reopened")
    } catch {
      toast.error("Failed to reopen")
    } finally {
      setReopeningIds((prev) => prev.filter((id) => id !== itemKey))
    }
  }, [reopenTask])

  // Follow-up items no longer use the completion modal — clicking them
  // navigates straight to the record so the follow-up can be resolved there.
  const handleNavigateToRecord = useCallback((item: TaskFeedItem) => {
    if (!item.recordGroupId || !item.recordId) return
    navigate(`/record-groups/${item.recordGroupId}/records/${item.recordId}`)
  }, [navigate])

  const handleComplete = useCallback((item: TaskFeedItem) => {
    if (item.type !== 'TASK') { handleNavigateToRecord(item); return }
    setActionItem(item)
    setActionMode('complete')
  }, [handleNavigateToRecord])

  const handleReopen = useCallback((item: TaskFeedItem) => {
    if (item.type !== 'TASK') { handleNavigateToRecord(item); return }
    setActionItem(item)
    setActionMode('reopen')
  }, [handleNavigateToRecord])

  const handleDelete = useCallback(async (item: TaskFeedItem) => {
    if (item.type !== "TASK") return
    const itemKey = `${item.type}-${item.id}`
    setDeletingIds((prev) => [...prev, itemKey])
    try {
      await deleteTask.mutateAsync(item.id)
      toast.success("Task deleted")
    } catch {
      toast.error("Failed to delete task")
    } finally {
      setDeletingIds((prev) => prev.filter((id) => id !== itemKey))
    }
  }, [deleteTask])

  const items = useMemo(() => feed?.items ?? [], [feed?.items])
  const completedViewItems = useMemo(() => items, [items])
  const visibleItems = section === 'completed' ? completedViewItems : items
  const sortedItems = [...visibleItems].sort((a, b) => {
    if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate)
    if (a.dueDate) return -1
    if (b.dueDate) return 1
    return 0
  })
  // Stable key so the batch permission query only refetches when the set of tasks changes.
  const taskIdKey = sortedItems.filter((item) => item.type === 'TASK').map((item) => item.id).join(',')
  const taskIds = useMemo(() => (taskIdKey ? taskIdKey.split(',') : []), [taskIdKey])
  const taskActions = useAllowedActionsBatch('TASK', taskIds)
  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900">Tasks</h1>
            <p className="mt-1 text-sm text-neutral-500">Follow-ups and reminders across your workspace</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw className={cn('mr-2 h-4 w-4', isLoading && 'animate-spin')} />
              Refresh
            </Button>
            {canCreateTask && (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                New Task
              </Button>
            )}
          </div>
        </div>

        <div className="mt-6 flex gap-1 rounded-lg bg-neutral-100 p-1">
          <button onClick={() => setSection('remaining')}
            className={cn('flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-all',
              section === 'remaining' ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-700')}>
            <Clock className="h-4 w-4" /> Remaining
          </button>
          <button onClick={() => setSection('completed')}
            className={cn('flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-all',
              section === 'completed' ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-700')}>
            <CheckCheck className="h-4 w-4" /> Completed
          </button>
        </div>

        <div className="mt-4 rounded-lg border border-neutral-200 bg-white p-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
              <FormattedDateInput
                id="tasks-date-from"
                label="From"
                value={dateFrom}
                max={dateTo}
                onChange={handleDateFromChange}
              />
              <FormattedDateInput
                id="tasks-date-to"
                label="To"
                value={dateTo}
                min={dateFrom}
                onChange={handleDateToChange}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={resetDateRange}
                disabled={isTodayRange}
                className="h-9"
              >
                <Calendar className="mr-2 h-4 w-4" />
                Today
              </Button>
            </div>
            <p className="text-xs text-neutral-500">
              {section === 'remaining' ? 'Due' : 'Completed'}: {rangeLabel}
            </p>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => <div key={i} className="h-20 animate-pulse rounded-lg bg-neutral-100" />)}
        </div>
      )}

      {isError && !isLoading && (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-destructive-200 bg-destructive-50 py-12 text-destructive-700">
          <AlertCircle className="h-10 w-10" />
          <p className="text-sm font-medium">Failed to load tasks</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>Try again</Button>
        </div>
      )}

      {!isLoading && !isError && sortedItems.length === 0 && (
        <div className="flex flex-col items-center gap-4 rounded-lg border border-dashed border-neutral-300 bg-white/50 py-16 text-neutral-400">
          <CheckSquare className="h-12 w-12" />
          <div className="text-center">
            <p className="font-medium text-neutral-500">No {section} items</p>
            <p className="mt-1 text-sm">
              {section === 'remaining'
                ? 'Create a task or add a follow-up to a record to get started'
                : 'Completed tasks will appear here'}
            </p>
          </div>
          {section === 'remaining' && canCreateTask && (
            <Button variant="default" size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create your first task
            </Button>
          )}
        </div>
      )}

      {!isLoading && !isError && sortedItems.length > 0 && (
        <FeedSection
          title={section === 'remaining' ? 'Pending' : 'Done'}
          icon={section === 'remaining' ? Clock : CheckCheck}
          items={sortedItems}
          onComplete={handleComplete}
          onReopen={handleReopen}
          onDelete={handleDelete}
          onNavigate={handleNavigateToRecord}
          canEditTask={(item) => taskActions.can(item.id, PERMISSION_KEYS.TASK_UPDATE)}
          canDeleteTask={(item) => taskActions.can(item.id, PERMISSION_KEYS.TASK_DELETE)}
          onEdit={(item) => {
            if (item.type === 'TASK') {
              setEditTask({
                id: item.id,
                workspaceId: '',
                creatorUserId: '',
                creatorName: '',
                title: item.title,
                description: item.description,
                visibilityScope: item.visibilityScope ?? 'ASSIGNED',
                dueDate: item.dueDate,
                completedAt: item.completedAt,
                createdAt: '',
                updatedAt: '',
                deleted: false,
                assigneeUserIds: item.assigneeUserIds,
                instructions: item.instructions,
              })
            }
          }}
          completingIds={completingIds}
          reopeningIds={reopeningIds}
          deletingIds={deletingIds}
        />
      )}

      <CreateTaskDialog open={createOpen} onOpenChange={setCreateOpen} />

      {editTask && (
        <EditTaskDialog
          task={editTask}
          open={!!editTask}
          onOpenChange={(open) => { if (!open) setEditTask(null) }}
        />
      )}

      <NotePromptDialog
        open={!!actionItem && !!actionMode}
        title={actionMode === 'reopen' ? 'Reopen task' : 'Mark task complete'}
        description={actionItem?.title ?? 'Add an optional note for this action.'}
        confirmLabel={actionMode === 'reopen' ? 'Reopen' : 'Complete'}
        showNextActivity={actionMode === 'reopen'}
        defaultNextActivityAt={actionItem?.dueDate ?? undefined}
        defaultNextActivityInstructions={actionItem?.instructions ?? undefined}
        loading={
          !!actionItem && (
            actionMode === 'complete'
              ? completingIds.includes(`${actionItem.type}-${actionItem.id}`)
              : reopeningIds.includes(`${actionItem.type}-${actionItem.id}`)
          )
        }
        onOpenChange={(open) => {
          if (!open) {
            setActionItem(null)
            setActionMode(null)
          }
        }}
        onConfirm={async (payload) => {
          if (!actionItem || !actionMode) return
          if (actionMode === 'complete') {
            await runComplete(actionItem, payload)
          } else {
            await runReopen(actionItem, payload)
          }
          setActionItem(null)
          setActionMode(null)
        }}
      />
    </div>
  )
}
